import classNames from 'classnames'
import styles from './Legend.module.scss'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import { PropsWithChildren } from 'react'
import { LegendContentProps, LegendIndicatorProps, LegendProps, LegendWrapperProps } from './types.ts'
import { Icon, Tooltip } from '@boltenergy-be/design-system'
import { DSChartColors } from 'types/colors.ts'

const Legend = ({ children, variant = 'list', boxed, title }: LegendWrapperProps) => {
  return (
    <div
      data-boxed={!!boxed}
      className={classNames(styles.totals, {
        [styles['mobile-list']]: variant === 'list-mobile',
        [styles.list]: variant === 'list'
      })}
    >
      {!!title && <strong className={styles.title}>{title}</strong>}
      {children}
    </div>
  )
}

const Indicator = (indicator: LegendIndicatorProps) => {
  if (indicator.icon) {
    return <Icon name={indicator.icon} className={classNames(styles.indicator, styles.icon, styles[indicator.color])} />
  }

  return (
    <>
      {/* FULL */}
      {indicator.full && (
        <div
          className={styles.indicator}
          style={{
            background: indicator.color === DSChartColors.TRANSPARENT ? 'transparent' : `var(--chart-${indicator.color})`
          }}
        />
      )}

      {/* STRIPPED */}
      {indicator.stripped && (
        <div
          className={styles.indicator}
          style={{
            background: `linear-gradient(-45deg, transparent 0 20%, var(--chart-${indicator.color}) 20% 40%, transparent 40% 60%, var(--chart-${indicator.color}) 60% 80%, transparent 80% 100%)`
          }}
        />
      )}
    </>
  )
}

const Content = ({ isLoading, children, label, addendum, indicator, variant, withTooltip }: LegendContentProps) => {
  const indicators = (Array.isArray(indicator) ? indicator : [indicator]).filter((entry) => !!entry)

  return (
    <section data-variant={variant || 'list'} className={classNames(styles.block)}>
      <header>
        {indicators.length > 0 && (
          <div className={styles['indicator-wrapper']}>
            {indicators.map((entry, index) => (
              <Indicator key={`${entry.icon ? 'icon' : entry.color}-${index}`} {...entry} />
            ))}
          </div>
        )}
        <strong className={classNames(styles.label, { [styles.underlined]: !!withTooltip })}>{label}</strong>
      </header>

      {isLoading ? (
        <LoadingSkeleton className={styles.loader}>
          <LoadingSkeleton.Rectangle height={20} width={140} />
        </LoadingSkeleton>
      ) : (
        <span className={styles.value}>{children}</span>
      )}

      {!!addendum && <small className={styles.addendum}>{addendum}</small>}
    </section>
  )
}

const LegendEntryWrapper = ({ tooltip, children, ...props }: PropsWithChildren<LegendProps>) => {
  return tooltip ? (
    <Tooltip asChild tooltipContent={tooltip} tooltipContentClassName={styles.tooltip}>
      <div>
        <Content {...props} withTooltip>
          {children || <span className={styles.empty}>—</span>}
        </Content>
      </div>
    </Tooltip>
  ) : (
    <Content {...props}>{children || <span className={styles.empty}>—</span>}</Content>
  )
}

Legend.Entry = LegendEntryWrapper

export default Legend
